import { auth } from './auth.js';
import { storage } from './storage.js';

/**
 * Sessão do usuário: guarda os tokens (vindos da nossa API) e renova o access token sozinha.
 *
 * Eventos (session.events):
 *   "expired" → o refresh token não vale mais (revogado/expirado); o app volta ao login.
 *   "change"  → entrou ou saiu.
 */

const KEY = 'tc.session';
const RENEW_BEFORE_MS = 60_000;

/** @type {{ email: string, username?: string, name?: string | null, accessToken: string, refreshToken: string, expiresAt: number } | null} */
let current = storage.get(KEY);
/** @type {Promise<string> | null} */
let refreshing = null;

const events = new EventTarget();
/** Tarefas antes de sair (ex.: desligar o push deste aparelho), ainda com o token válido. */
const beforeSignOut = [];

function save(value) {
  current = value;
  if (value) storage.set(KEY, value);
  else storage.remove(KEY);
  events.dispatchEvent(new Event('change'));
}

async function refresh() {
  if (!current) throw new Error('sem sessão');
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const r = await auth.refresh(current.refreshToken, current.username);
      save({ ...current, accessToken: r.accessToken, expiresAt: Date.now() + r.expiresIn * 1000 });
      return current.accessToken;
    } catch (err) {
      if (err?.code === 'SESSION_EXPIRED') expire();
      throw err;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

function expire() {
  if (!current) return;
  save(null);
  events.dispatchEvent(new Event('expired'));
}

export const session = {
  events,

  get isLoggedIn() {
    return Boolean(current?.refreshToken);
  },

  get email() {
    return current?.email ?? null;
  },

  /** Nome vindo do Google (preenche o cadastro); null no login por senha. */
  get name() {
    return current?.name ?? null;
  },

  async signIn(email, password) {
    const r = await auth.login(email, password);
    save({ email, username: r.username, accessToken: r.accessToken, refreshToken: r.refreshToken, expiresAt: Date.now() + r.expiresIn * 1000 });
  },

  /** Login com Google: tokens já trocados pela API (tela /entrar). */
  signInWithTokens(r) {
    save({ email: r.email, username: r.username, name: r.name ?? null, accessToken: r.accessToken, refreshToken: r.refreshToken, expiresAt: Date.now() + r.expiresIn * 1000 });
  },

  /** Access token válido (renova se estiver perto de expirar). */
  async accessToken() {
    if (!current) return null;
    if (Date.now() < current.expiresAt - RENEW_BEFORE_MS) return current.accessToken;
    return refresh();
  },

  /** Força a renovação (ex.: a API respondeu 401). */
  forceRefresh: refresh,

  /** Sessão inválida detectada pela API. */
  expire,

  /** @param {() => Promise<void>} fn */
  onBeforeSignOut(fn) {
    beforeSignOut.push(fn);
  },

  async signOut() {
    for (const fn of beforeSignOut) {
      // no máximo 3 s cada: sem rede, sai do mesmo jeito
      await Promise.race([Promise.resolve().then(fn).catch(() => {}), new Promise((r) => setTimeout(r, 3000))]);
    }
    const token = current?.refreshToken;
    save(null);
    if (token) await auth.logout(token).catch(() => {});
  },
};
